/** Curated avatar and voice options for Agent Hub personalities. */

export const MAX_AGENTS = 3;

export const VOICE_SAMPLE_MAX_LENGTH = 70;

export const defaultVoiceSample = (displayName: string): string => {
  const name = displayName.trim();
  const text = name
    ? `Hi, I'm ${name}, your shopping advisor. What can I help you with today?`
    : "Hi, I'm your shopping advisor. What can I help you with today?";
  return text.slice(0, VOICE_SAMPLE_MAX_LENGTH);
};

export const AGENT_AVATARS = [
  {
    id: 'avatar-1',
    label: 'Avatar 1',
    src: 'https://cdn.skipify.com/ella-sdk/agentic-avatar-1.png',
  },
  {
    id: 'avatar-2',
    label: 'Avatar 2',
    src: 'https://cdn.skipify.com/ella-sdk/agentic-avatar-2.png',
  },
] as const;

export const CUSTOM_AVATAR_OPTION_ID = 'custom-upload';
export const CUSTOM_AVATAR_LABEL = 'Custom upload';

export const isCatalogAvatarSrc = (src: string): boolean =>
  AGENT_AVATARS.some((avatar) => avatar.src === src);

export const isCustomAvatarSrc = (src: string): boolean => Boolean(src) && !isCatalogAvatarSrc(src);

/** POST /agentic-settings/avatars returns `avatarSrc` (also accept `src` / `avatar.src`). */
export const uploadedAvatarSrcFromResponse = (response: {
  avatarSrc?: string;
  src?: string;
  avatar?: { src?: string };
}): string => response.avatarSrc || response.src || response.avatar?.src || '';

export type AgentVoice = {
  profileId: string;
  label: string;
};

/** Builds Profile 1..N from voice profile IDs returned by GET agentic-settings/voice-profiles. */
export const voicesFromProfileIds = (profileIds?: readonly string[]): AgentVoice[] =>
  (profileIds ?? [])
    .map((id) => id.trim())
    .filter(Boolean)
    .map((profileId, index) => ({
      profileId,
      label: `Profile ${index + 1}`,
    }));

export const DEFAULT_AGENT_AVATAR_SRC = AGENT_AVATARS[0].src;
export const DEFAULT_AGENT_VOICE_PROFILE_ID = '';

export const resolveVoiceProfileId = (
  voiceProfileId: string | undefined,
  availableProfileIds?: readonly string[],
): string => {
  const trimmed = voiceProfileId?.trim() ?? '';
  if (trimmed) {
    return trimmed;
  }

  return (availableProfileIds ?? []).map((id) => id.trim()).find(Boolean) ?? '';
};

export const avatarLabelForSrc = (src: string): string =>
  AGENT_AVATARS.find((avatar) => avatar.src === src)?.label ??
  (src ? CUSTOM_AVATAR_LABEL : 'Custom avatar');

export const voiceLabelForProfileId = (
  profileId: string,
  voices: readonly AgentVoice[] = [],
): string => voices.find((voice) => voice.profileId === profileId)?.label ?? 'Current voice';
